
import Vue from 'vue'
import Header from './Header.vue'
import SearchResults from './Search/SearchResults.vue'
import HeroCarousel from './HeroCarousel.vue'
import CategoriesList from './CategoriesList.vue'
import ActivitiesList from './ActivitiesList.vue'
import ActivityTile from './ActivityTile.vue'
import HTMLBlock from './HTMLBlock.vue'
import ActivityPlayer from './ActivityPlayer.vue'
import Loading from './Loading.vue'
import ActivityArticle from './ActivityArticle.vue'
import { PortalTarget } from 'portal-vue'
import {
  ProjectState,
  ProjectRow,
  ProjectActivity,
  ProjectPath,
  URLParameterRange_ActivityType,
} from '../channel.types'
import { find, findIndex } from 'lodash'

import { mapGetters, mapState } from 'vuex'

export default Vue.extend({
  data() {
    return {
      scrollTop: 0,
    }
  },

  computed: {
    ...mapGetters([
      'activeRows',
      'activities',
      'isPathSelected',
      'isSelectedPathAnArticle',
    ]),
    ...mapState({
      timer: state => (<ProjectState>state).timer,
      search_query: state => (<ProjectState>state).search_query,
      played_activity: state => (<ProjectState>state).played_activity,
      projectActivities: state => (<ProjectState>state).project.activities,
    }),
    showRowsList: function() {
      const { search_query, played_activity } = this.$store.state
      return (
        search_query === null &&
        played_activity === null &&
        !this.isSelectedPathAnArticle
      )
    },
    isLoading() {
      return this.$store.state.project === null
    },
  },

  methods: {
    rowAndActivityParamsExist(rowId, activityId) {
      return (rowId || rowId === 0) && (activityId || activityId === 0)
    },

    //This will open up an activity and play it. Also, handle the next() thing.
    handleNavigationToActivity(
      rowId,
      activityId,
      activityType,
      activityPlay,
      next
    ) {
      if (this.rowAndActivityParamsExist(rowId, activityId)) {
        const row = find(this.activeRows, { id: rowId }) as ProjectRow
        const rowIndex = findIndex(this.activeRows, { id: rowId })

        //TODO. We have a handy getter activities that does some filtering. It won't work for HERO_CAROUSEL rows because
        //HERO_CAROUSEL has no row_tile_type. I may decide to just only use projectActivities here.
        const activities =
          row.type == 'HERO_CAROUSEL'
            ? this.projectActivities
            : this.activities(row.row_tile_type, row.id)

        const activity = find(activities, { id: activityId }) as ProjectActivity
        const activityIndex = findIndex(activities, { id: activityId })

        this.$store.commit('setPlayedActivity', null)
        this.$store.commit('setPath', null)

        Vue.nextTick(() => {
          this.$store.commit(
            'setPath',
            this.getProjectPath(row, rowIndex, activity, activityIndex)
          )

          //the hero-carousel doesn't do an expand. It's sort of already expanded.
          //so if this is a hero-carousel row, just don't do this.
          if (row.type != 'HERO_CAROUSEL') {
            if (row.is_enabled_second_screen) {
              this.$store.dispatch('handle_expand_click')
            }
          }

          if (activityPlay) {
            this.$store.dispatch('handle_activity_play_click')
          }

          if (next) {
            next()
          }
        })
      } else {
        if (next) {
          next()
        }
      }
    },

    getProjectPath(row, row_index, activity, activity_index) {
      return {
        row,
        row_index,
        activity,
        activity_index,
      }
    },
  },

  mounted() {
    const slug = this.$route.params.channelSlug
    const store = this.$store

    store
      .dispatch('init', slug)
      .then(() => {
        store.dispatch('track_visit')
        store.dispatch('fetch_viewer_data')
        store.dispatch('init_timer')
        store.dispatch('inject_css')
      })
      .then(() => {
        const rowId = parseInt(this.$route.params.rowId)
        const activityId = parseInt(this.$route.params.activityId)
        const activityType = this.$route.params.activityType
        const activityPlay = this.$route.params.activityPlay == 'play'
        this.handleNavigationToActivity(
          rowId,
          activityId,
          activityType,
          activityPlay
        )
      })
  },

  components: {
    Header,
    HeroCarousel,
    CategoriesList,
    ActivitiesList,
    ActivityTile,
    SearchResults,
    HTMLBlock,
    ActivityPlayer,
    PortalTarget,
    ActivityArticle,
    Loading,
  },

  //this and beforeRouteUpdate are the main brain which dispatches all the actions needed to transition
  //from one type of url to another. whenever you change the url structure, you have to deal with this function.
  beforeRouteLeave(to, from, next) {
    const fromRowId = parseInt(from.params.rowId)
    const fromActivityId = parseInt(from.params.activityId)
    const fromActivityPlay = from.params.activityPlay == 'play'

    const toRowId = parseInt(to.params.rowId)
    const toActivityId = parseInt(to.params.activityId)
    const toActivityType = to.params.activityType
    const toActivityPlay = to.params.activityPlay == 'play'

    //moving from /c/network-comparison/ to /c/network-comparison/rowId/channelId
    if (
      !this.rowAndActivityParamsExist(fromRowId, fromActivityId) &&
      this.rowAndActivityParamsExist(toRowId, toActivityId)
    ) {
      this.handleNavigationToActivity(
        toRowId,
        toActivityId,
        toActivityType,
        toActivityPlay
      )
    }

    //moving from /c/network-comparison/rowId/channelId/[play] to /c/network-comparison/
    else if (
      this.rowAndActivityParamsExist(fromRowId, fromActivityId) &&
      !this.rowAndActivityParamsExist(toRowId, toActivityId)
    ) {
      if (fromActivityPlay) {
        this.$store.commit('setPlayedActivity', null) //Stop the current video from playing.
      }

      this.$store.dispatch('handle_collapse_click') //Close down the current activity.
    }

    //moving from /c/network-comparison/rowId/channelId/[play] to moving from /c/network-comparison/rowId/channelId/[play]
    else if (
      this.rowAndActivityParamsExist(fromRowId, fromActivityId) &&
      this.rowAndActivityParamsExist(toRowId, toActivityId)
    ) {
      if (!fromActivityPlay) {
        this.$store.commit('setPlayedActivity', null) //Stop the current video from playing.
      }

      this.handleNavigationToActivity(
        toRowId,
        toActivityId,
        toActivityType,
        toActivityPlay
      )
    }

    next()
  },

  //This will catch the route moving from /c/network-comparison/rowId/channelId to /c/network-comparison/rowId/channelId
  beforeRouteUpdate(to, from, next) {
    const rowId = parseInt(to.params.rowId)
    const activityId = parseInt(to.params.activityId)
    const activityType = to.params.activityType
    const activityPlay = from.params.activityPlay == 'play'
    this.handleNavigationToActivity(
      rowId,
      activityId,
      activityType,
      activityPlay,
      next
    )

    //TODO should make this promise chaining work.
    //this.handleNavigation(rowId, activityId, next)
    // .then(() => {
    //   console.log("NEXT!")
    //   next()
    // })
  },
  //////////////////////////////

  watch: {
    showRowsList(new_value, old_value) {
      if (new_value === false) {
        this.scrollTop = window.scrollY
      } else if (new_value === true) {
        Vue.nextTick(() => {
          window.scrollTo({
            top: this.scrollTop,
          })
        })
      }
    },
  },
})
